import React, { useState, useEffect } from "react";
import * as tf from "@tensorflow/tfjs";
import axios from "axios";
import breedNames from './class_names.json';
import { Search, Shuffle, Upload } from 'lucide-react';

const App = () => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [results, setResults] = useState([]);
  const [randomImage, setRandomImage] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [model, setModel] = useState(null);
  const [modelLoading, setModelLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);

  // Load the TensorFlow.js model from the public directory
  useEffect(() => {
    const loadModelWithProgress = async () => {
      const loadingPercentage = document.getElementById('loading-percentage');

      try {
        setModelLoading(true);

        // Load the model with progress tracking
        const loadedModel = await tf.loadGraphModel("/tfjs_model/model.json", {
          onProgress: (progress) => {
            const percentage = Math.round(progress * 100);
            if (loadingPercentage) {
              loadingPercentage.textContent = `${percentage}%`;
            }
          },
        });

        // Warm up the model with a dummy inference
        const dummyTensor = tf.zeros([1, 224, 224, 3]);
        await loadedModel.predict(dummyTensor).data();
        dummyTensor.dispose();
        
        console.log("Model loaded and warmed up successfully");
        setModel(loadedModel);
      } catch (err) {
        console.error("Failed to load model", err);
        setError("Failed to load the model. Please try again.");
      } finally {
        setModelLoading(false);
      }
    };

    loadModelWithProgress();
  }, []);

  const handleFileChange = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      setLoading(true);
      setSelectedFile(file);
      setResults([]);
      setError("");
      setRandomImage("");
      
      // Create and set the preview URL
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);

      if (model) {
        const imageData = await preprocessImage(file);
        await predictBreed(imageData);
      } else {
        throw new Error("Model not loaded");
      }
    } catch (err) {
      console.error("Error processing file:", err);
      setError("Error processing the image. Please try again.");
    } finally {
      setLoading(false);
      // Reset the file input value so the same file can be selected again
      event.target.value = '';
    }
  };

  const preprocessImage = async (imageFile) => {
    const img = document.createElement("img");
    img.src = URL.createObjectURL(imageFile);

    return new Promise((resolve) => {
      img.onload = () => {
        const tensor = tf.browser
          .fromPixels(img)
          .resizeBilinear([224, 224]) // Resize to the input size the model expects
          .expandDims(0) // Add batch dimension
          .div(255); // Normalize the image
        resolve(tensor);
      };
    });
  };

  const predictBreed = async (imageData) => {
    try {
      setLoading(true);
      // Get predictions immediately
      const predictions = await model.predict(imageData).data();
      
      // Start preloading the top prediction's icon while waiting for the artificial delay
      const predictedClasses = Array.from(predictions)
        .map((confidence, idx) => ({
          breed: breedNames[idx],
          confidence,
        }))
        .sort((a, b) => b.confidence - a.confidence);
      
      // Preload the top breed's icon
      const topBreed = predictedClasses[0].breed;
      const img = new Image();
      img.src = `/img/icons/${formatBreedForIcon(topBreed)}.svg`;
      
      // Random delay between 600ms and 1200ms
      const randomDelay = Math.random() * 1000 + 400;
      await new Promise(resolve => setTimeout(resolve, randomDelay));

      setResults(predictedClasses.slice(0, 3));

      const resultsBox = document.querySelector('#results');
      if (resultsBox) {
        const rect = resultsBox.getBoundingClientRect();
        const isInViewport = rect.top >= 0 && rect.bottom <= window.innerHeight;
        if (!isInViewport) {
          window.scrollTo({
            top: window.scrollY + rect.top - 40,
            behavior: 'smooth'
          });
        }
      }
    } catch (err) {
      console.error("Error during prediction:", err);
      setError("Error occurred during the prediction. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleImageClick = async (imageUrl) => {
    try {
      setLoading(true);
      // Fetch and preprocess the image as a blob
      const response = await fetch(imageUrl);
      const blob = await response.blob();

      const imageData = await preprocessImage(blob);
      await predictBreed(imageData);
    } catch (err) {
      console.error("Error uploading image:", err);
      setError("Error occurred while uploading the image. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Add this helper function before the return statement
  const formatBreedForIcon = (breed) => {
    return breed.toLowerCase().replace(/ /g, '_');
  };

  const fetchRandomImage = async () => {
    try {
      const response = await axios.get(
        "https://dog.ceo/api/breeds/image/random"
      );
      setRandomImage(response.data.message);
    } catch (err) {
      console.error("Error fetching random image:", err);
    }
  };

  // Add this useEffect after fetchRandomImage
  useEffect(() => {
    if (randomImage && model) {
      handleImageClick(randomImage);
    }
  }, [randomImage]); // Trigger when randomImage changes

  // Add this helper function at the component level
  const getInteractionText = () => {
    return (
      <>
        <span className="pointer-only">Click to</span>
        <span className="touch-only">Tap to</span>
      </>
    );
  };

  return (
    <div className="app text-center py-4 px-6 sm:px-10 md:px-20 max-w-[720px] mx-auto rounded-2xl">
      <h1>
        <svg viewBox="0 0 500 100" className="mx-auto w-full max-w-[500px] h-auto">
          <defs>
              <path id="curve" d="M50,100 Q250,50 450,100" fill="transparent" />
          </defs>
          <text fontSize="48" fill="#162720" textAnchor="middle">
              <textPath href="#curve" startOffset="50%">WHAT'S THAT DOG?</textPath>
          </text>
        </svg>
      </h1>
      <h2 className="text-xl min-[440px]:text-2xl mt-[-.25em]">The Dog Breed Classifier</h2>

      {modelLoading && (
        <div className="text-lg mt-12">
          <p>Loading app [<span id="loading-percentage">0%</span>]</p>
        </div>
      )}

      {!modelLoading && (
        <>
          <div className="flex flex-col min-[480px]:flex-row justify-center items-center gap-4 mt-8 min-[480px]:mt-16 mb-10">
            <div 
              className="flex w-full max-w-[300px] aspect-[3/2] min-[480px]:aspect-square rounded-lg bg-white/70 backdrop-blur-sm relative hover:text-[#9B6F97]"
              onClick={(e) => e.stopPropagation()}
            >
              <label 
                htmlFor="file-upload" 
                className="flex flex-1 flex-col overflow-hidden rounded-lg cursor-pointer relative"
              >
                {previewUrl ? (
                  <div className="flex-1 relative w-full h-full flex flex-col overflow-hidden">
                    <img
                      src={previewUrl}
                      alt="Preview"
                      className="w-full h-full object-cover"
                    />
                    <button 
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        const fileInput = document.getElementById('file-upload');
                        if (fileInput) {
                          fileInput.value = '';
                          fileInput.click();
                        }
                      }}
                      className="absolute bottom-0 w-full px-4 py-2 bg-[#9B6F97] hover:bg-[#90648D] text-white disabled:bg-gray-500 disabled:text-gray-300 text-lg flex items-center justify-center gap-2"
                    >
                      Upload another image <Upload size={16} />
                    </button>
                  </div>
                ) : (
                  <div className="w-full h-full flex flex-col items-center justify-center gap-2">
                    <div>{getInteractionText()} upload an image</div>
                    <Upload size={32} />
                  </div>
                )}
                <input 
                  type="file"
                  id="file-upload"
                  accept="image/*" 
                  onChange={handleFileChange}
                  className="sr-only" 
                  disabled={loading || !model}
                />
              </label>
            </div>

            <div className="relative z-10 flex items-center justify-center">
              <div className="absolute bg-[#CBE1D8] rounded-full w-12 h-12 flex items-center justify-center">
                <span className="font-bold uppercase">Or</span>
              </div>
            </div>

            <div 
              className="flex flex-col overflow-hidden w-full max-w-[300px] aspect-[3/2] min-[480px]:aspect-square rounded-lg bg-white/70 backdrop-blur-sm relative cursor-pointer hover:text-[#9B6F97]"
              onClick={(e) => {
                e.stopPropagation();
                if (!loading) {
                  setPreviewUrl(null);
                  setSelectedFile(null);
                  fetchRandomImage();
                }
              }}
            >
              {randomImage ? (
                <>
                  <img
                    src={randomImage}
                    alt="Random Dog"
                    className="w-full h-full object-cover"
                  />
                  <div className="flex w-full">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        if (!loading) {
                          fetchRandomImage();
                        }
                      }}
                      className="absolute bottom-0 w-full flex-1 px-4 py-2 bg-[#9B6F97] hover:bg-[#90648D] text-white disabled:bg-gray-500 disabled:text-gray-300 text-lg flex items-center justify-center gap-2"
                      disabled={loading}
                    >
                      Shuffle <Shuffle size={16} />
                    </button>
                  </div>
                </>
              ) : (
                <div className="w-full h-full flex flex-col items-center justify-center gap-2">
                  <div>{getInteractionText()} use a random image</div>
                  <Shuffle size={32} />
                </div>
              )}
            </div>
          </div>

          {error && <p className="text-red-500">{error}</p>}

          {loading || results.length > 0 ? (
            <div id="results" className="flex justify-center min-[480px]:justify-start mt-16 mx-auto rounded-lg p-6 max-w-[300px] min-[480px]:max-w-full min-h-[360px] min-[480px]:min-h-[240px] bg-white/70 backdrop-blur-sm relative">
              <h2 className="text-lg uppercase font-bold text-center bg-[#CBE1D8] transform rotate-[-2deg] p-2 absolute top-[-20px] left-1/2 transform -translate-x-1/2">
                Results
              </h2>
              {loading ? (
                <div class="flex flex-1 items-center justify-center">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600 mr-2"></div>
                  <p>Analyzing...</p>
                </div>
              ) : (
                <>
                  <div className="flex flex-col min-[480px]:flex-row items-center gap-2 sm:gap-8">
                    <img 
                      src={`/img/icons/${formatBreedForIcon(results[0].breed)}.svg`}
                      alt={`${results[0].breed} icon`}
                      className="max-[479px]:mt-4 w-32 h-32 sm:w-48 sm:h-48"
                    />
                    <div className="text-left">
                      <h2 className="text-4xl font-bold mt-2 min-[480px]:mt-4">{results[0].breed}</h2>
                      <p className="text-lg mt-2">{(results[0].confidence * 100).toFixed(2)}% confidence</p>
                      <div className="flex space-x-1 w-[80px]">
                        <div className={`h-[5px] rounded-full w-full ${results[0].confidence < 0.6 ? 'bg-red-500' : results[0].confidence < 0.9 ? 'bg-orange-500' : 'bg-green-500'}`}></div>
                        <div className={`h-[5px] rounded-full w-full ${results[0].confidence < 0.6 ? 'bg-gray-400' : results[0].confidence < 0.9 ? 'bg-orange-500' : 'bg-green-500'}`}></div>
                        <div className={`h-[5px] rounded-full w-full ${results[0].confidence < 0.6 ? 'bg-gray-400' : results[0].confidence < 0.9 ? 'bg-gray-400' : 'bg-green-500'}`}></div>
                      </div>
                      <button
                        onClick={() => setIsModalOpen(true)}
                        className="px-2 py-1 rounded-lg bg-[#9B6F97] hover:bg-[#90648D] text-white text-lg mt-6"
                      >
                        Show full results
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          ) : null}

          {/* Modal */}
          {isModalOpen && (
            <div 
              className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
              onClick={() => setIsModalOpen(false)}
            >
              <div 
                className="bg-white/95 backdrop-blur-md p-6 rounded-lg max-w-md w-full mx-4"
                onClick={e => e.stopPropagation()}
              >
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-xl font-bold">Full Results</h3>
                  <button
                    onClick={() => setIsModalOpen(false)}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    ✕
                  </button>
                </div>
                <ul className="space-y-4">
                  {results.map((result, index) => (
                    <li key={index} className="flex gap-4">
                      <img 
                        src={`/img/icons/${formatBreedForIcon(result.breed)}.svg`}
                        alt={`${result.breed} icon`}
                        className="w-20 h-20"
                      />
                      <div className="flex flex-col items-start justify-center">
                        <div className="text-xl font-semibold">{result.breed}</div>
                        <div className="text-gray-600">
                          {(result.confidence * 100).toFixed(2)}% confidence
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default App;
